'use client';

import { type IDefaultFormBuilderProps } from '@/components/Form/Fields/types';
import Label from '@/components/Label/Label';
import InfoIconModal from '@/components/infoIcon/InfoIconModal';
import PostcodeInputCity from '@/components/postcodeInput/PostcodeInputCity';
import { RichText } from '@/components/richText/RichText';
import { SwdCircularProgress } from '@/components/swdCircularProgress/SwdCircularProgress';
import { TextField } from '@/components/textField';
import { hasContent } from '@/helper/RichText';
import { usePostcodeSelect } from '@/hooks/usePostcodeSelect';
import type { FormTextStoryblok } from '@/types/component-types-sb';
import { type FieldProps, useFormikContext } from 'formik';
import { type FC, useEffect } from 'react';

import type { FormValues } from '../../types';

interface IInputField extends Omit<FormTextStoryblok, 'id'>, FieldProps, IDefaultFormBuilderProps {}

const InputField: FC<IInputField> = ({
  cityField,
  field,
  id,
  label,
  placeholder,
  infoText,
  errorMessage,
  isInvalid,
  required,
  disabled,
  readOnly,
  errorMessage: errorMessageProp,
  infoText: infoTextProp,
  isInvalid: isInvalidProp,
  required: requiredProp,
}) => {
  const state = usePostcodeSelect();
  const { values, handleChange, setFieldValue } = useFormikContext<FormValues>();
  useEffect(() => {
    if (cityField) {
      setFieldValue(cityField as string, state.selectedValue.cityName);
    }
  }, [state.selectedValue.cityName, cityField]);

  return (
    <>
      <TextField
        {...field}
        id={id}
        disabled={disabled}
        placeholder={placeholder}
        label={label && <Label text={label} />}
        helperText={isInvalid && errorMessage}
        error={isInvalid}
        fullWidth
        required={required}
        autoComplete="off"
        onChange={(e: React.FocusEvent<any, Element>) => {
          state.handleInputChange(e);
          setFieldValue(values[cityField] as string, state.selectedValue.cityName);
          handleChange(e);
        }}
        InputProps={{
          endAdornment: (
            <>
              {hasContent(infoText) ? (
                <InfoIconModal>
                  <RichText text={infoText} />
                </InfoIconModal>
              ) : undefined}
              {state.loading && <SwdCircularProgress size={16} />}
            </>
          ),
          readOnly,
          inputProps: {
            tabIndex: readOnly ? -1 : 0,
          },
        }}
        inputProps={{ 'data-testid': 'form-builder-post-code' }}
      />
      <PostcodeInputCity state={state} onlyDialog={true} />
    </>
  );
};

export default InputField;
